import type { Skill } from '../domain.js';

/**
 * Англійська мова. Орієнтир: A1 для 3–4 класу, A1+/A2 для 5–6.
 * Лексика генерується з тематичного словника, граматика — з шаблонів,
 * читання й аудіювання — з курованого банку.
 */
export const ENGLISH_SKILLS: Skill[] = [
  // ── Лексика ─────────────────────────────────────────────────────────────
  {
    id: 'english.vocab.everyday',
    subject: 'english',
    strand: 'vocab',
    title: 'Базова лексика',
    description: 'Знає слова щоденних тем: родина, дім, школа, їжа, одяг.',
    gradeBand: [3, 5],
    prerequisites: [],
    baseDifficulty: 850,
    foundational: true,
    generators: ['eng.vocabTranslate'],
  },
  {
    id: 'english.vocab.extended',
    subject: 'english',
    strand: 'vocab',
    title: 'Розширена лексика',
    description: 'Володіє темами: подорожі, спорт, професії, природа, технології.',
    gradeBand: [5, 6],
    prerequisites: ['english.vocab.everyday'],
    baseDifficulty: 1120,
    generators: ['eng.vocabTranslate'],
  },
  {
    id: 'english.vocab.collocations',
    subject: 'english',
    strand: 'vocab',
    title: 'Сталі словосполучення',
    description: 'Обирає природне поєднання слів: do homework, make a cake.',
    gradeBand: [5, 6],
    prerequisites: ['english.vocab.everyday'],
    baseDifficulty: 1180,
    generators: ['eng.collocations'],
  },
  {
    id: 'english.spelling.basic',
    subject: 'english',
    strand: 'spelling',
    title: 'Правопис слів',
    description: 'Пише вивчені слова без помилок, розрізняє схожі написання.',
    gradeBand: [3, 6],
    prerequisites: ['english.vocab.everyday'],
    baseDifficulty: 1000,
    generators: ['eng.spelling'],
  },

  // ── Граматика ───────────────────────────────────────────────────────────
  {
    id: 'english.grammar.tobe',
    subject: 'english',
    strand: 'grammar',
    title: 'Дієслово to be',
    description: 'Правильно вживає am / is / are в різних типах речень.',
    gradeBand: [3, 4],
    prerequisites: [],
    baseDifficulty: 820,
    foundational: true,
    generators: ['eng.toBe'],
  },
  {
    id: 'english.grammar.plurals_articles',
    subject: 'english',
    strand: 'grammar',
    title: 'Множина й артиклі',
    description: 'Утворює множину, зокрема винятки; обирає a / an / the.',
    gradeBand: [3, 5],
    prerequisites: ['english.grammar.tobe'],
    baseDifficulty: 950,
    generators: ['eng.pluralsArticles'],
  },
  {
    id: 'english.grammar.present_simple',
    subject: 'english',
    strand: 'grammar',
    title: 'Present Simple',
    description: 'Вживає теперішній простий, зокрема -s у третій особі.',
    gradeBand: [3, 5],
    prerequisites: ['english.grammar.tobe'],
    baseDifficulty: 980,
    foundational: true,
    generators: ['eng.presentSimple'],
  },
  {
    id: 'english.grammar.present_continuous',
    subject: 'english',
    strand: 'grammar',
    title: 'Present Continuous',
    description: 'Утворює й відрізняє тривалий час від простого.',
    gradeBand: [4, 6],
    prerequisites: ['english.grammar.present_simple'],
    baseDifficulty: 1080,
    generators: ['eng.presentContinuous'],
  },
  {
    id: 'english.grammar.past_simple',
    subject: 'english',
    strand: 'grammar',
    title: 'Past Simple',
    description: 'Знає другу форму правильних і неправильних дієслів.',
    gradeBand: [4, 6],
    prerequisites: ['english.grammar.present_simple'],
    baseDifficulty: 1140,
    foundational: true,
    generators: ['eng.pastSimple', 'eng.irregularVerbs'],
  },
  {
    id: 'english.grammar.future',
    subject: 'english',
    strand: 'grammar',
    title: 'Майбутній час',
    description: 'Вживає will і be going to відповідно до ситуації.',
    gradeBand: [5, 6],
    prerequisites: ['english.grammar.present_continuous'],
    baseDifficulty: 1180,
    generators: ['eng.future'],
  },
  {
    id: 'english.grammar.prepositions',
    subject: 'english',
    strand: 'grammar',
    title: 'Прийменники',
    description: 'Обирає in / on / at / under / between за змістом.',
    gradeBand: [3, 6],
    prerequisites: ['english.vocab.everyday'],
    baseDifficulty: 1010,
    generators: ['eng.prepositions'],
  },
  {
    id: 'english.grammar.comparatives',
    subject: 'english',
    strand: 'grammar',
    title: 'Ступені порівняння',
    description: 'Утворює вищий і найвищий ступінь прикметників.',
    gradeBand: [4, 6],
    prerequisites: ['english.vocab.everyday'],
    baseDifficulty: 1090,
    generators: ['eng.comparatives'],
  },
  {
    id: 'english.grammar.modals',
    subject: 'english',
    strand: 'grammar',
    title: 'Модальні дієслова',
    description: 'Вживає can / must / should / have to за значенням.',
    gradeBand: [5, 6],
    prerequisites: ['english.grammar.present_simple'],
    baseDifficulty: 1160,
    generators: ['eng.modals'],
  },
  {
    id: 'english.grammar.questions',
    subject: 'english',
    strand: 'grammar',
    title: 'Питальні речення',
    description: 'Будує питання з правильним порядком слів і допоміжним дієсловом.',
    gradeBand: [4, 6],
    prerequisites: ['english.grammar.present_simple'],
    baseDifficulty: 1130,
    generators: ['eng.questions'],
  },

  // ── Мовлення ────────────────────────────────────────────────────────────
  {
    id: 'english.reading.comprehension',
    subject: 'english',
    strand: 'reading',
    title: 'Читання з розумінням',
    description: 'Розуміє короткий текст і відповідає на питання за ним.',
    gradeBand: [4, 6],
    prerequisites: ['english.vocab.everyday'],
    baseDifficulty: 1140,
  },
  {
    id: 'english.listening.basic',
    subject: 'english',
    strand: 'listening',
    title: 'Аудіювання',
    description: 'Розуміє на слух слова й короткі фрази (озвучення в застосунку).',
    gradeBand: [3, 6],
    prerequisites: ['english.vocab.everyday'],
    baseDifficulty: 1070,
    generators: ['eng.listening'],
  },
];
